import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackdropBlurBottom } from "@/components/ui/backdrop-blur";
import { Card, CardContent } from "@/components/ui/card";
import ImageWithFallback from "@/components/ui/image-with-fallback";
import type { EnrichedProfile } from "@/lib/supabase/types";
import { cn, getProfileLink, slice } from "@/lib/utils";
import { Link2Icon, MapPin } from "lucide-react";
import Link from "next/link";

interface ProfileCardProps {
	profile: EnrichedProfile | null;
	className?: string;
	asCard?: boolean;
}

export function ProfileCard({
	profile,
	className,
	asCard = false,
}: ProfileCardProps) {
	return (
		<Card className={cn("flex flex-col w-full rounded-2xl", className)}>
			{profile?.cover_image_url && (
				<div className="relative w-full rounded-3xl overflow-hidden">
					<ImageWithFallback
						src={profile?.cover_image_url}
						width={600}
						height={200}
						alt={profile?.name || ""}
						className="w-full h-[320px] opacity-80 object-cover"
					/>
					<BackdropBlurBottom />
				</div>
			)}
			<CardContent
				className={cn(
					asCard ? "rounded-2xl" : "p-0",
					"flex flex-row h-full w-full items-center justify-between",
				)}
			>
				<div className="flex flex-col py-2 mt-auto w-full mx-auto items-center justify-center">
					<div
						className={cn(
							"z-50 flex h-full w-full justify-center items-center",
							asCard ? "py-4 gap-2" : "p-4 gap-4",
						)}
					>
						<div
							className={cn(
								"h-full flex flex-col items-center justify-center gap-2 ",
								profile?.cover_image_url ? "mt-[-100px]" : "",
								asCard ? "" : "w-full",
							)}
						>
							<div className="relative">
								<Avatar className="size-32 border-2">
									<AvatarImage
										src={profile?.avatar_url || "/images/logo.png"}
										width={128}
										height={128}
									/>
									<AvatarFallback>
										{profile?.name?.charAt(0) || "Z"}
									</AvatarFallback>
								</Avatar>
							</div>
							<div
								className={cn(
									"w-full h-full flex flex-col justify-between gap-1 mb-2",
									asCard ? "items-start" : "items-center text-center",
								)}
							>
								<div className="flex flex-col gap-1">
									<h1
										className={cn(
											"font-extrabold",
											asCard ? "text-lg" : "text-2xl lg:text-3xl ",
										)}
									>
										{slice(profile?.name, 20)}
									</h1>
									{profile?.username && (
										<p className="text-link">
											<Link
												href={getProfileLink(profile?.username || profile?.id)}
												className="font-semibold hover:underline hover:underline-offset-4"
											>
												@{profile?.username}
											</Link>
										</p>
									)}
									{profile?.description && (
										<p className="text-sm">
											{slice(profile?.description, 25)}..
										</p>
									)}
								</div>
								<div className="text-primary h-full flex flex-col items-center gap-1 text-base">
									{!asCard && profile?.website && (
										<Link
											href={profile?.website}
											className="text-sm text-link hover:underline hover:underline-offset-4 flex items-center gap-1"
											target="_blank"
											rel="noopener noreferrer"
											prefetch={false}
										>
											<Link2Icon className="size-4" />
											Website
										</Link>
									)}
									{profile?.city && (
										<div className="flex items-center gap-1 text-base text-primary">
											<MapPin className="size-4" />
											{profile?.city?.name}
										</div>
									)}
									{/* {profile?.created_at && !asCard && (
										<div className="flex items-center justify-end gap-1">
											<span className="text-xs text-nowrap">
												Joined {timeAgo(new Date(profile?.created_at))}
											</span>
										</div>
									)} */}
								</div>
							</div>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
